import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API_URL } from "../../../config";

function LeavePage() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  }, []);

  const [employee, setEmployee] = useState(null);
  const [leaveList, setLeaveList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [leave, setLeave] = useState({
    leave_type: "",
    leave_category: "",
    from_date: "",
    to_date: "",
    reason: "",
  });

  const loadEmployeeLeaves = async (empId) => {
    const res = await axios.get(`${API_URL}/api/getAllLeaves/${empId}`);
    setLeaveList(res.data || []);
  };

  const exportRows = (rows, fileName) => {
    const worksheetData = rows.map((lv, index) => ({
      "S.No": index + 1,
      "Leave ID": lv.leaveId,
      "Employee Name": employee?.ename,
      "Leave Type": `${lv.leave_type} (${lv.leave_category})`,
      "From Date": new Date(lv.from_date).toDateString(),
      "To Date": new Date(lv.to_date).toDateString(),
      "Total Days": lv.days,
      "Paid/Unpaid": lv.paid ? "Paid" : "Unpaid",
      Status: lv.status,
    }));
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leaves");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      fileName
    );
  };

  const exportToExcel = () => {
    if (!leaveList.length) return alert("No data to export");
    exportRows(leaveList, "LeaveReport.xlsx");
  };

  const exportSelectedMonth = () => {
    const filteredData = leaveList.filter((lv) => {
      const d = new Date(lv.from_date);
      return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
    });
    if (!filteredData.length) return alert("No data to export for selected month");
    exportRows(filteredData, `LeaveReport_${selectedYear}_${selectedMonth + 1}.xlsx`);
  };

  useEffect(() => {
    if (!user?.employeeId) return;
    axios
      .get(`${API_URL}/api/getEmpDataByID/${user.employeeId}`)
      .then((res) => setEmployee(res.data))
      .catch((err) => console.log(err));
  }, [user]);

  useEffect(() => {
    if (!employee) return;
    loadEmployeeLeaves(employee.employeeId)
      .finally(() => setLoading(false))
      .catch((err) => console.log(err));
  }, [employee]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "leave_type") {
      if (value === "Half Day") {
        setLeave((prev) => ({ ...prev, leave_type: value, to_date: prev.from_date }));
      } else if (value === "Short Leave") {
        const today = new Date().toISOString().split("T")[0];
        setLeave((prev) => ({ ...prev, leave_type: value, from_date: today, to_date: today, leave_category: "Short Leave" }));
      } else {
        setLeave((prev) => ({ ...prev, leave_type: value }));
      }
      return;
    }
    if (name === "from_date" && leave.leave_type === "Half Day") {
      setLeave((prev) => ({ ...prev, from_date: value, to_date: value }));
      return;
    }
    setLeave((prev) => ({ ...prev, [name]: value }));
  };

  const hasShortLeaveThisMonth = useMemo(() => {
    const month = new Date().getMonth();
    const year = new Date().getFullYear();
    return leaveList.some((lv) => {
      const d = new Date(lv.from_date);
      return (lv.leave_category === "Short Leave" || lv.leave_type === "Short Leave") && d.getMonth() === month && d.getFullYear() === year;
    });
  }, [leaveList]);

  const submitLeave = async (e) => {
    e.preventDefault();
    if (!employee) return;
    try {
      const holidayRes = await axios.get(`${API_URL}/api/holiday`);
      const holidays = holidayRes.data;
      const isHoliday = holidays.some((h) => {
        const holidayDate = new Date(h.date).toDateString();
        const fromDate = new Date(leave.from_date).toDateString();
        const toDate = new Date(leave.to_date).toDateString();
        return holidayDate === fromDate || holidayDate === toDate;
      });
      if (isHoliday) return alert("You cannot apply leave on a Holiday");

      await axios.post(`${API_URL}/api/addLeave`, {
        employeeId: employee.employeeId,
        leave_type: leave.leave_type,
        leave_category: leave.leave_category,
        from_date: leave.from_date,
        to_date: leave.to_date,
        reason: leave.reason,
        status: "Pending",
      });

      alert("Leave applied successfully");
      setShowModal(false);
      // Auto-switch filter to the newly applied leave month/year.
      const appliedFromDate = new Date(leave.from_date);
      if (!Number.isNaN(appliedFromDate.getTime())) {
        setSelectedMonth(appliedFromDate.getMonth());
        setSelectedYear(appliedFromDate.getFullYear());
      }

      await loadEmployeeLeaves(employee.employeeId);
      setLeave({ leave_type: "", leave_category: "", from_date: "", to_date: "", reason: "" });
    } catch (err) {
      console.log(err);
      alert(err.response?.data?.message || "Error applying leave");
    }
  };

  const monthFilteredLeaves = leaveList.filter((lv) => {
    const d = new Date(lv.from_date);
    return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
  });

  return (
    <div>
      <h2 className="emp-page-title">Leave Dashboard</h2>
      <p className="emp-page-subtitle">Apply, track, and export your leave history with status updates.</p>

      <section className="emp-panel">
        <div className="emp-panel-body">
          <div className="emp-toolbar">
            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                + New Leave
              </button>
              <button className="btn btn-outline-secondary" onClick={exportToExcel}>
                Export All
              </button>
              <button className="btn btn-outline-success" onClick={exportSelectedMonth}>
                Export Month
              </button>
            </div>

            <div className="d-flex gap-2">
              <select className="form-select w-auto" value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                {[
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].map((month, index) => (
                  <option value={index} key={index}>
                    {month}
                  </option>
                ))}
              </select>

              <select className="form-select w-auto" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                {Array.from({ length: 5 }).map((_, i) => {
                  const y = new Date().getFullYear() - i;
                  return (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="emp-table-wrap">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Leave ID</th>
                  <th>Leave Date</th>
                  <th>Duration</th>
                  <th>Type</th>
                  <th>Paid</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Admin Note</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : monthFilteredLeaves.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      No leave found for this month
                    </td>
                  </tr>
                ) : (
                  monthFilteredLeaves.map((lv, i) => (
                    <tr key={i}>
                      <td>{employee?.ename || "-"}</td>
                      <td>{lv.leaveId}</td>
                      <td>{new Date(lv.from_date).toLocaleDateString()}</td>
                      <td>{lv.leave_type}</td>
                      <td>{lv.leave_category}</td>
                      <td>
                        {lv.paid ? (
                          <span className="emp-soft-badge success">Paid</span>
                        ) : (
                          <span className="emp-soft-badge danger">Unpaid</span>
                        )}
                      </td>
                      <td>
                        {lv.status === "Approved" ? (
                          <span className="emp-soft-badge success">Approved</span>
                        ) : lv.status === "Rejected" ? (
                          <span className="emp-soft-badge danger">Rejected</span>
                        ) : (
                          <span className="emp-soft-badge warning">Pending</span>
                        )}
                      </td>
                      <td>{lv.reason}</td>
                      <td>{lv.adminNote || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {showModal && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Apply for Leave</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <form onSubmit={submitLeave}>
                <div className="modal-body">
                  <label>Leave Duration</label>
                  <select name="leave_type" className="form-control mb-2" value={leave.leave_type} onChange={handleChange} required>
                    <option value="">Select Duration</option>
                    <option value="Full Day">Full Day</option>
                    <option value="Half Day">Half Day</option>
                    {!hasShortLeaveThisMonth ? (
                      <option value="Short Leave">Short Leave</option>
                    ) : (
                      <option value="Short Leave" disabled>
                        Short Leave (Already Taken This Month)
                      </option>
                    )}
                  </select>

                  <label>Leave Category</label>
                  <select
                    name="leave_category"
                    className="form-control mb-2"
                    value={leave.leave_category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {!hasShortLeaveThisMonth ? (
                      <option value="Short Leave">Short Leave</option>
                    ) : (
                      <option value="Short Leave" disabled>
                        Short Leave (Already Taken This Month)
                      </option>
                    )}
                    <option value="Sick Leave">Sick Leave</option>
                    <option value="Casual Leave">Casual Leave</option>
                  </select>

                  {leave.leave_type !== "Short Leave" && (
                    <>
                      <label>From Date</label>
                      <input
                        type="date"
                        className="form-control mb-2"
                        name="from_date"
                        value={leave.from_date}
                        onChange={handleChange}
                        required
                      />

                      <label>To Date</label>
                      <input
                        type="date"
                        className="form-control mb-2"
                        name="to_date"
                        value={leave.to_date}
                        disabled={leave.leave_type === "Half Day"}
                        onChange={handleChange}
                        required
                      />
                    </>
                  )}

                  <label>Reason</label>
                  <input type="text" className="form-control mb-2" name="reason" value={leave.reason} onChange={handleChange} required />
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary">Apply</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeavePage;
